import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { isEqual } from 'lodash';
import NewValueContainer from '../../search/widgets/filters/new-value-container';
import CheckBoxOption from '../../search/widgets/filters/checkbox-option';
import MenuList from '../../search/widgets/filters/MenuList';

export interface FacetObject {
  value: string,
  label: string,
}

interface Props {
  options: Array<FacetObject>,
  extraCss?: string,
  placeholder: string,
  label: string,
  selectedValue: Array<FacetObject>,
  setSelectedValue: (selectedValue: Array<FacetObject>) => void,
}

const CustomAgeFilter: React.FC<Props> = ({
  options,
  extraCss,
  placeholder,
  label,
  selectedValue,
  setSelectedValue,
}) => {
  // Set component length based on the placeholder
  const length = `${(9 * placeholder.length) + 60}px`;

  // Custom sub-component styles
  const selectStyles = {
    control: (styles) => {
      return {
        ...styles,
        width: length,
      };
    },
    menuList: (styles) => {
      return {
        ...styles,
        paddingTop: 0,
        paddingBottom: 0,
      };
    },
    option: (styles) => {
      return {
        ...styles,
        backgroundColor: 'white',
        color: 'inherit',
      };
    },
  };

  const containerRef = useRef<HTMLDivElement>(null);
  // Is the select box focused (and open?)
  const [isFocused, setIsFocused] = useState(false);

  /**
   * Open or close the menu any time a click event is registered. Based
   * on the following conditions:
   *
   * Close the component and reset to empty search if
   * 1) The click is outside the component
   * 2) The menu (options dropdown) is not open
   * 3) The click is outside the menu
   *
   * Otherwise, keep it open
   */
  const onDomClick = (e) => {
    if (!containerRef || !containerRef.current) {
      return;
    }

    const menu = containerRef.current.querySelector('.algolia-select__menu');

    if (
      !containerRef.current.contains(e.target)
      || !menu
      || !menu.contains(e.target)
    ) {
      setIsFocused(false);
    } else {
      setIsFocused(true);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', onDomClick);

    return () => {
      document.removeEventListener('mousedown', onDomClick);
    };
  }, []);

  return (
    <div ref={containerRef}>
      <Select
        aria-label={label || placeholder}
        className={`algolia-select ${extraCss}`}
        classNamePrefix="algolia-select"
        value={selectedValue.length > 0 ? selectedValue[0] : selectedValue}
        components={{
          ValueContainer: NewValueContainer,
          Option: CheckBoxOption,
          MenuList,
        }}
        onChange={(event) => {
          setIsFocused(false);
          if (event) {
            const currentValue = selectedValue.length > 0 ? selectedValue[0] : selectedValue;
            if (isEqual(currentValue, event)) {
              setSelectedValue([]);
            } else {
              setSelectedValue([event]);
            }
          }
        }}
        isSearchable={false}
        options={options}
        placeholder={placeholder}
        styles={selectStyles}
        name={label}
        menuIsOpen={isFocused || undefined}
        isFocused={isFocused || undefined}
        setIsFocused={setIsFocused}
      />
    </div>
  );
};

export default CustomAgeFilter;
